import { APIFieldAttributes, APIFieldType, ArdoqId } from '@ardoq/api-types';
import { SelectOption } from '@ardoq/select';
import { Checkbox } from '@ardoq/forms';
import { formatNumber, Locale } from '@ardoq/locale';
import { formatDateShort, formatDateTimeShort } from '@ardoq/date-time';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';
import {
  SurveyWarningMessage,
  SurveyErrorMessage,
} from '../surveyQuestionAtoms';
import {
  SurveyReadOnlyQuestion,
  SurveyReadOnlyLabel,
} from '../SurveyReadOnlyQuestion';
import { GetUserFeedbackReturn } from '../getUserFeedback';
import { isValueEmpty, isValueStringOrEmpty } from '../../utils/stringUtils';
import { isArray, isNumber } from 'lodash';
import { FieldAnswerValueWithoutDateRange } from '../../types';
import { urlUtils, isShiftXUrl } from '@ardoq/common-helpers';
import { Link } from '@ardoq/link';
import { MarkdownViewer } from '@ardoq/markdown';

type FieldRendererProps = {
  type: APIFieldType;
  attributeName?: string;
  value: FieldAnswerValueWithoutDateRange | undefined;
  field: APIFieldAttributes | undefined;
  currentLocale: Locale;
  i18nLocale?: I18nLocale;
  parentName: string | undefined;
  hideReadOnlyLabel: boolean;
  userOptions: SelectOption<ArdoqId>[];
};

const FieldRenderer = ({
  type,
  attributeName,
  value,
  field,
  currentLocale,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  parentName,
  hideReadOnlyLabel,
  userOptions,
}: FieldRendererProps) => {
  if (type === APIFieldType.TEXT_AREA && isValueStringOrEmpty(value)) {
    return (
      <SurveyReadOnlyQuestion
        hideReadOnlyLabel={hideReadOnlyLabel}
        i18nLocale={i18nLocale}
      >
        <MarkdownViewer
          content={
            value || t(i18nLocale, 'surveysUi:noValueSet', 'No value set.')
          }
          sanitationLevel="permissive"
        />
      </SurveyReadOnlyQuestion>
    );
  } else if (
    [APIFieldType.LIST, APIFieldType.SELECT_MULTIPLE_LIST].includes(type) &&
    (isArray(value) || isValueStringOrEmpty(value))
  ) {
    if (!value) {
      return (
        <SurveyReadOnlyQuestion
          hideReadOnlyLabel={hideReadOnlyLabel}
          i18nLocale={i18nLocale}
        >
          {t(i18nLocale, 'surveysUi:noValueSelected', 'No value selected.')}
        </SurveyReadOnlyQuestion>
      );
    }
    if (value instanceof Array) {
      return (
        <SurveyReadOnlyQuestion
          hideReadOnlyLabel={hideReadOnlyLabel}
          i18nLocale={i18nLocale}
        >
          {value.join(', ')}
        </SurveyReadOnlyQuestion>
      );
    }
    return (
      <SurveyReadOnlyQuestion
        hideReadOnlyLabel={hideReadOnlyLabel}
        i18nLocale={i18nLocale}
      >
        {value}
      </SurveyReadOnlyQuestion>
    );
  } else if (type === APIFieldType.USER && isValueStringOrEmpty(value)) {
    return (
      <SurveyReadOnlyQuestion
        hideReadOnlyLabel={hideReadOnlyLabel}
        i18nLocale={i18nLocale}
      >
        {value
          ? (userOptions.find(option => option.value === value)?.label ??
            t(i18nLocale, 'surveysUi:unknownUser', 'Unknown user'))
          : t(i18nLocale, 'surveysUi:noUserSelected', 'No user selected.')}
      </SurveyReadOnlyQuestion>
    );
  } else if (type === APIFieldType.DATE_ONLY && isValueStringOrEmpty(value)) {
    return (
      <SurveyReadOnlyQuestion
        hideReadOnlyLabel={hideReadOnlyLabel}
        i18nLocale={i18nLocale}
      >
        {value && formatDateShort(value, currentLocale)}
      </SurveyReadOnlyQuestion>
    );
  } else if (type === APIFieldType.DATE_TIME && isValueStringOrEmpty(value)) {
    return (
      <SurveyReadOnlyQuestion
        hideReadOnlyLabel={hideReadOnlyLabel}
        i18nLocale={i18nLocale}
      >
        {value && formatDateTimeShort(value, currentLocale)}
      </SurveyReadOnlyQuestion>
    );
  } else if (type === APIFieldType.URL && isValueStringOrEmpty(value)) {
    if (!value) {
      return (
        <SurveyReadOnlyQuestion
          hideReadOnlyLabel={hideReadOnlyLabel}
          i18nLocale={i18nLocale}
        >
          {value}
        </SurveyReadOnlyQuestion>
      );
    }
    const url = urlUtils.maybeRelativeUrl(value, isShiftXUrl(value));
    return (
      <SurveyReadOnlyQuestion
        hideReadOnlyLabel={hideReadOnlyLabel}
        i18nLocale={i18nLocale}
      >
        <Link
          href={url}
          target="_blank"
          rel="noreferrer noopener"
          typography="text2"
        >
          {value}
        </Link>
      </SurveyReadOnlyQuestion>
    );
  } else if (
    type === APIFieldType.CHECKBOX &&
    (typeof value === 'boolean' || isValueEmpty(value))
  ) {
    return (
      <>
        <Checkbox
          data-test-id="checkbox"
          isChecked={value === undefined ? null : value}
          isDisabled
        />
        {hideReadOnlyLabel ? null : (
          <SurveyReadOnlyLabel i18nLocale={i18nLocale} />
        )}
      </>
    );
  } else if (
    type === APIFieldType.NUMBER &&
    (isNumber(value) || isValueEmpty(value))
  ) {
    return (
      <SurveyReadOnlyQuestion
        hideReadOnlyLabel={hideReadOnlyLabel}
        i18nLocale={i18nLocale}
      >
        {formatNumber(value, field?.numberFormatOptions, currentLocale)}
      </SurveyReadOnlyQuestion>
    );
  } else if (attributeName === 'parent') {
    if (!value) {
      return (
        <SurveyReadOnlyQuestion
          hideReadOnlyLabel={hideReadOnlyLabel}
          i18nLocale={i18nLocale}
        >
          {t(i18nLocale, 'surveysUi:noneSelected', 'None selected.')}
        </SurveyReadOnlyQuestion>
      );
    }
    return (
      <SurveyReadOnlyQuestion
        hideReadOnlyLabel={hideReadOnlyLabel}
        i18nLocale={i18nLocale}
      >
        {parentName}
      </SurveyReadOnlyQuestion>
    );
  }
  return (
    <SurveyReadOnlyQuestion
      hideReadOnlyLabel={hideReadOnlyLabel}
      i18nLocale={i18nLocale}
    >
      {value}
    </SurveyReadOnlyQuestion>
  );
};

export const SurveyReadOnlyOrLockedFieldQuestion = ({
  userFeedback,
  type,
  attributeName,
  value,
  field,
  currentLocale,
  i18nLocale,
  parentName,
  hideReadOnlyLabel,
  userOptions,
}: FieldRendererProps & {
  userFeedback: GetUserFeedbackReturn;
}) => {
  return (
    <>
      <FieldRenderer
        type={type}
        field={field}
        currentLocale={currentLocale}
        i18nLocale={i18nLocale}
        attributeName={attributeName}
        value={value}
        parentName={parentName}
        hideReadOnlyLabel={hideReadOnlyLabel}
        userOptions={userOptions}
      />
      {userFeedback?.warning && (
        <SurveyWarningMessage>{userFeedback.warning}</SurveyWarningMessage>
      )}
      {userFeedback?.error && (
        <SurveyErrorMessage>{userFeedback.error}</SurveyErrorMessage>
      )}
    </>
  );
};
